import { useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@monksflow/monks-ui';
import { projectFolderQuery } from '@/entities/project-folder';
import { SettledContent, SETTLED_STATE } from '@/shared/ui/animations';
import { useDelayedFlag } from '@/shared/hooks';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ApprovalLayout } from './components/ApprovalLayout';
import { GRID_CLASS, SKELETON_CARD_COUNT } from './components/AssetGrid';
import { FileAssetCardSkeleton } from './components/FileAssetCard';
import { FolderPane } from './components/FolderPane';
import { expandFolderPath } from './model';
import type { ProjectDetailContext } from './folders.types';
import { useTranslation } from 'react-i18next';

/**
 * One folder inside the approval section.
 *
 * The rail renders outside the loading and error branches: it is the section's
 * navigation, and unmounting it while the next folder loads would collapse the
 * tree on every selection.
 */
const PANE_SKELETON_CARDS = Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => index);

export function FolderDetailPage() {
  const { t } = useTranslation('projects');

  const { project, openCreateFolderModal } = useOutletContext<ProjectDetailContext>();
  const { folderId } = useParams<{ folderId: string }>();
  const projectId = project.platformProjectId;

  const {
    data: folder,
    isLoading: isFolderLoading,
    error: folderError,
  } = useQuery(projectFolderQuery(projectId, folderId));

  // A link into a nested folder lands with the rail collapsed around it, so
  // open the branches on the way down to it.
  const ancestorIds = folder?.breadcrumbs.map((crumb) => crumb.id);
  useEffect(() => {
    if (ancestorIds) expandFolderPath(projectId, ancestorIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a fresh array each render would re-run this forever
  }, [projectId, ancestorIds?.join()]);

  // Nothing announces loading until it has lasted long enough to be worth it,
  // the same threshold the other sections wait out.
  const showLoadingIndicator = useDelayedFlag(isFolderLoading);

  // A folder holding nothing is reported as empty rather than as rows, which is
  // what makes the skeleton dissolve into it instead of being cut. Handing over
  // to an empty answer is the one transition worth waiting through.
  const isEmptyFolder = !!folder && folder.foldersCount === 0 && folder.fileCount === 0;

  const paneState =
    folderError || (!isFolderLoading && !folder)
      ? SETTLED_STATE.error
      : isFolderLoading
        ? SETTLED_STATE.loading
        : isEmptyFolder
          ? SETTLED_STATE.empty
          : SETTLED_STATE.rows;

  const renderPane = () => {
    if (paneState === SETTLED_STATE.loading) {
      // Drawn as a folder of assets, which is what most folders are. A folder
      // of folders will re-lay its body once it lands; waiting for certainty
      // instead meant showing a bare header, which read as a broken page for
      // the whole fetch.
      //
      // It appears at once. The grid draws this same placeholder again the
      // moment the folder lands, so an entrance here plays, gets replaced
      // mid-stagger by an identical block, and reads as a blink. The one
      // animation worth having is the last one: the placeholder dissolving
      // into the rows.
      //
      // Mirrors `FolderPane`'s frame rather than approximating it, so the real
      // pane arrives into the shape already on screen.
      return !showLoadingIndicator ? null : (
        <div className="flex min-h-0 flex-1 flex-col">
          <header className="flex h-10 items-center gap-md">
            <Skeleton className="size-10 shrink-0 rounded-lg" />
            <Skeleton className="h-7 w-64 rounded-lg" />
            <Skeleton className="ml-auto h-10 w-36 shrink-0 rounded-lg" />
          </header>

          <div className="mt-section flex min-h-0 flex-1 flex-col gap-section">
            <div className="flex flex-row items-center gap-xs">
              <Skeleton className="h-10 w-80 rounded-lg" />
              <Skeleton className="h-10 w-24 rounded-lg" />
              <Skeleton className="h-10 w-36 rounded-lg" />
            </div>

            <div className={GRID_CLASS}>
              {PANE_SKELETON_CARDS.map((card) => (
                <FileAssetCardSkeleton key={card} />
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (paneState === SETTLED_STATE.error || !folder) {
      return (
        <EmptyState
          title={t('projectDetail.folderDetailPage.folderNotFound')}
          description={t('projectDetail.folderDetailPage.thisFolderCouldNotBe')}
        />
      );
    }

    return (
      <FolderPane projectId={projectId} folder={folder} onCreateSubfolder={openCreateFolderModal} />
    );
  };

  return (
    <ApprovalLayout projectId={projectId} onCreateFolder={() => openCreateFolderModal()}>
      {/* The card holds still while its contents change hands, the way the
          briefs toolbar stays put over its table. Only what is inside the
          surface crossfades. */}
      <div className="flex min-h-0 flex-1 flex-col rounded-lg bg-card p-section">
        <SettledContent
          state={paneState}
          className="min-h-0 flex-1"
          contentClassName="flex min-h-0 flex-1 flex-col"
        >
          {renderPane()}
        </SettledContent>
      </div>
    </ApprovalLayout>
  );
}
